export const M4_UNIT_18 = {
  id: "m4-u18-choices",
  title: "Unidad 18: Making Choices / Haciendo Elecciones",
  topic: "Learn to ask and answer choice questions! Master WHICH vs WHAT, use AND/OR to connect options, and learn irregular comparatives (better, worse, farther) and superlatives (best, worst, farthest). Make decisions like a native speaker!",
  iconType: "choice",
  grammarImage: "/grammar/grammar-m4-u18.jpg",
  grammarText: "WHICH vs WHAT: WHICH = limited/specific options ('Which city - A or B?'). WHAT = open/general question ('What is your favorite?'). OR = shows choice between options. AND = connects multiple things. IRREGULAR COMPARATIVES: good→better (NOT gooder!), bad→worse (NOT badder!), far→farther/further. IRREGULAR SUPERLATIVES: good→the best, bad→the worst, far→the farthest/furthest. ADVANCED: Either...or (one or the other), Neither...nor (not this, not that), Both...and (this and that together). Use present simple for facts and preferences!",
  
  sets: [
    // SET 1: WHICH vs WHAT Introduction
    {
      id: "m4-u18-set1",
      title: "Set 1: WHICH vs WHAT - Basic Difference",
      questions: [
        {
          type: "match",
          q: "Match question word to use:",
          pairs: [
            { left: "WHICH (limited options)", right: "Which city - A or B?" },
            { left: "WHAT (open/general)", right: "What is your favorite?" },
            { left: "OR (shows choice)", right: "Coffee or tea?" }
          ],
          a: "completed",
          tip: "WHICH = specific choice from limited options! WHAT = open question! OR = shows the options!"
        },
        {
          type: "mc",
          q: "'___ city do you prefer - Toronto or Montreal?' (2 specific options)",
          options: ["Which", "What", "Where"],
          a: "Which",
          tip: "WHICH! Two specific cities = limited choice! 'Toronto OR Montreal' = clear options, use WHICH!"
        },
        {
          type: "input",
          q: "Complete: '___ is your favorite color?' (open - many possible colors)",
          a: "What"
        },
        {
          type: "order",
          q: "Ordena: '¿Cuál prefieres - el rojo o el azul?'",
          words: ["Which", "do", "you", "prefer", "-", "red", "or", "blue?"],
          a: "Which do you prefer - red or blue?"
        },
        {
          type: "mc",
          q: "'___ do you want to eat?' (general - not limiting choices)",
          options: ["What", "Which", "Who"],
          a: "What",
          successtip: "✓ WHAT! Open question! Many possible answers - not limiting to specific options. 'What do you want?' = general!"
        },
        {
          type: "input",
          q: "Complete: '___ hotel is cheaper - the Ritz or the Plaza?' (Which)",
          a: "Which"
        },
        {
          type: "mc",
          q: "'___ season do you like - summer or winter?' (2 specific seasons)",
          options: ["Which", "What", "When"],
          a: "Which",
          tip: "WHICH! Two specific options = summer OR winter. Limited choice = use WHICH!"
        },
        {
          type: "order",
          q: "Ordena: '¿Qué te gusta hacer en tu tiempo libre?'",
          words: ["What", "do", "you", "like", "to", "do", "in", "your", "free", "time?"],
          a: "What do you like to do in your free time?"
        },
        {
          type: "input",
          q: "Complete: '___ movie do you want to watch - action or comedy?' (Which)",
          a: "Which"
        },
        {
          type: "mc",
          q: "Key rule: When to use WHICH vs WHAT?",
          options: [
            "WHICH = limited/specific options (A or B?). WHAT = open/general (many options)",
            "They mean exactly the same thing",
            "Always use WHAT, never WHICH"
          ],
          a: "WHICH = limited/specific options (A or B?). WHAT = open/general (many options)",
          successtip: "✓ RULE MASTERED! 🎯 WHICH = 'pick from THESE specific options' (Which - A or B?). WHAT = 'tell me anything' (What do you like?). Both work in many cases, but WHICH is more specific!"
        }
      ]
    },

    // SET 2: Using OR in Questions
    {
      id: "m4-u18-set2",
      title: "Set 2: Using OR to Show Choices",
      questions: [
        {
          type: "match",
          q: "Match OR usage patterns:",
          pairs: [
            { left: "A or B?", right: "Simple choice between two" },
            { left: "A, B, or C?", right: "Choice between three or more" },
            { left: "Which... or...?", right: "WHICH + OR = specific choice question" }
          ],
          a: "completed",
          tip: "OR shows the options! Can be 2 options (A or B) or more (A, B, or C)!"
        },
        {
          type: "mc",
          q: "'Do you want coffee ___ tea?'",
          options: ["or", "and", "but"],
          a: "or",
          tip: "OR! Showing choice! ☕🍵 'Coffee or tea?' = pick one! OR = choice between options!"
        },
        {
          type: "input",
          q: "Complete: 'Which city is bigger - Montreal ___ Vancouver?' (or)",
          a: "or"
        },
        {
          type: "order",
          q: "Ordena: '¿Prefieres verano o invierno?'",
          words: ["Do", "you", "prefer", "summer", "or", "winter?"],
          a: "Do you prefer summer or winter?"
        },
        {
          type: "mc",
          q: "'Which restaurant do you like - Italian, Chinese, ___ Mexican?'",
          options: ["or", "and", "but"],
          a: "or",
          successtip: "✓ OR for multiple! Three options = Italian, Chinese, OR Mexican. OR works for 2+ choices!"
        },
        {
          type: "input",
          q: "Complete: 'Is it hot ___ cold outside?' (or)",
          a: "or"
        },
        {
          type: "mc",
          q: "'Do you live in Toronto ___ Montreal?' (asking which one)",
          options: ["or", "and", "but"],
          a: "or",
          tip: "OR! Asking which city! Not asking if you live in BOTH. OR = choice/alternative!"
        },
        {
          type: "order",
          q: "Ordena: '¿Quieres agua o jugo?'",
          words: ["Do", "you", "want", "water", "or", "juice?"],
          a: "Do you want water or juice?"
        },
        {
          type: "input",
          q: "Complete: 'Which is better - this ___ that?' (or)",
          a: "or"
        },
        {
          type: "mc",
          q: "Pattern: 'Which + [noun] + [verb] + A or B?'",
          options: [
            "Correct! 'Which city do you prefer - A or B?' is the pattern",
            "Wrong pattern",
            "Never use this"
          ],
          a: "Correct! 'Which city do you prefer - A or B?' is the pattern",
          successtip: "✓ PATTERN LEARNED! Structure = 'Which + noun + verb + option A or option B?' Natural way to ask choice questions!"
        }
      ]
    },

    // SET 3: Using AND to Connect Options
    {
      id: "m4-u18-set3",
      title: "Set 3: Using AND to Connect Multiple Things",
      questions: [
        {
          type: "match",
          q: "Match AND usage:",
          pairs: [
            { left: "A and B", right: "Connecting two things together" },
            { left: "I like A and B", right: "Expressing you like BOTH" },
            { left: "Both A and B", right: "Emphasizing TWO things together" }
          ],
          a: "completed",
          tip: "AND connects! Use when talking about BOTH/MULTIPLE things, not choosing!"
        },
        {
          type: "mc",
          q: "'I like coffee ___ tea.' (expressing I like BOTH)",
          options: ["and", "or", "but"],
          a: "and",
          tip: "AND! Connecting both! I like coffee AND tea = I like BOTH drinks!"
        },
        {
          type: "input",
          q: "Complete: 'Toronto ___ Montreal are big cities.' (and)",
          a: "and"
        },
        {
          type: "order",
          q: "Ordena: 'Me gustan el verano y el invierno'",
          words: ["I", "like", "summer", "and", "winter."],
          a: "I like summer and winter."
        },
        {
          type: "mc",
          q: "'Which cities do you visit - Toronto ___ Montreal?' (you visit BOTH)",
          options: ["and", "or", "but"],
          a: "and",
          successtip: "✓ AND for BOTH! Context matters! If visiting BOTH cities = AND. If choosing ONE = OR. Different meanings!"
        },
        {
          type: "input",
          q: "Complete: 'She speaks English ___ French.' (and)",
          a: "and"
        },
        {
          type: "mc",
          q: "Difference: 'Coffee or tea?' vs 'Coffee and tea?'",
          options: [
            "'OR' = choose one. 'AND' = both together",
            "They mean the same",
            "No difference"
          ],
          a: "'OR' = choose one. 'AND' = both together",
          tip: "KEY DIFFERENCE! ☕🍵 'Coffee OR tea?' = pick one. 'Coffee AND tea?' = do you want both? Meaning changes!"
        },
        {
          type: "order",
          q: "Ordena: 'Ella es inteligente y amable'",
          words: ["She", "is", "smart", "and", "kind."],
          a: "She is smart and kind."
        },
        {
          type: "input",
          q: "Complete: 'I like mountains ___ beaches.' (and - I like both)",
          a: "and"
        },
        {
          type: "mc",
          q: "'Both Toronto ___ Montreal are beautiful cities.'",
          options: ["and", "or", "but"],
          a: "and",
          successtip: "✓ BOTH...AND! Emphatic structure! 'Both A and B' = emphasizing TWO things together. More emphasis than just 'A and B'!"
        }
      ]
    },

    // SET 4: Irregular Comparatives
    {
      id: "m4-u18-set4",
      title: "Set 4: Irregular Comparatives (good/bad/far)",
      questions: [
        {
          type: "match",
          q: "Match irregular comparatives (MEMORIZE - don't follow -ER rule!):",
          pairs: [
            { left: "good → better", right: "NOT gooder! Irregular!" },
            { left: "bad → worse", right: "NOT badder! Irregular!" },
            { left: "far → farther/further", right: "NOT farer! Irregular!" }
          ],
          a: "completed",
          tip: "IRREGULAR! These DON'T follow the -ER rule! MUST memorize! Most common mistake!"
        },
        {
          type: "mc",
          q: "'This restaurant is good, but that one is ___.' (más bueno)",
          options: ["better", "gooder", "more good"],
          a: "better",
          tip: "BETTER! Irregular! ❌ NOT 'gooder'! ❌ NOT 'more good'! Good → better → best!"
        },
        {
          type: "input",
          q: "Complete: 'Today is bad, but yesterday was ___.' (worse)",
          a: "worse"
        },
        {
          type: "order",
          q: "Ordena: 'Mi inglés es mejor ahora'",
          words: ["My", "English", "is", "better", "now."],
          a: "My English is better now."
        },
        {
          type: "mc",
          q: "'The weather is bad today, but tomorrow is ___.' (peor)",
          options: ["worse", "badder", "more bad"],
          a: "worse",
          successtip: "✓ WORSE! Irregular! ❌ NOT 'badder'! ❌ NOT 'more bad'! Bad → worse → worst!"
        },
        {
          type: "input",
          q: "Complete: 'Vancouver is ___ from Toronto than Montreal is.' (farther/further)",
          a: "farther / further"
        },
        {
          type: "mc",
          q: "'Which hotel has ___ reviews - A or B?' (mejores reseñas)",
          options: ["better", "gooder", "more good"],
          a: "better",
          tip: "BETTER! Comparative with WHICH! 'Which has better reviews?' = common question pattern!"
        },
        {
          type: "order",
          q: "Ordena: 'Este examen es peor que el anterior'",
          words: ["This", "test", "is", "worse", "than", "the", "last", "one."],
          a: "This test is worse than the last one."
        },
        {
          type: "input",
          q: "Complete: 'Is Toronto ___ than Montreal?' (better)",
          a: "better"
        },
        {
          type: "mc",
          q: "Why are these called 'irregular'?",
          options: [
            "They don't follow the -ER rule. You must memorize them!",
            "They follow all normal rules",
            "They don't exist"
          ],
          a: "They don't follow the -ER rule. You must memorize them!",
          successtip: "✓ IRREGULAR MASTERY! These 3 are the most important irregulars! 🏆 Good→better, bad→worse, far→farther/further. Native speakers use these CONSTANTLY! NEVER say 'gooder' or 'badder'!"
        }
      ]
    },

    // SET 5: Irregular Superlatives
    {
      id: "m4-u18-set5",
      title: "Set 5: Irregular Superlatives (best/worst/farthest)",
      questions: [
        {
          type: "match",
          q: "Match irregular superlatives (MEMORIZE!):",
          pairs: [
            { left: "good → the best", right: "NOT the goodest! Irregular!" },
            { left: "bad → the worst", right: "NOT the baddest! Irregular!" },
            { left: "far → the farthest/furthest", right: "NOT the farest! Irregular!" }
          ],
          a: "completed",
          tip: "SUPERLATIVE IRREGULARS! These DON'T follow the -EST rule! Critical to memorize!"
        },
        {
          type: "mc",
          q: "'This is ___ restaurant in the city!' (el mejor)",
          options: ["the best", "the goodest", "the most good"],
          a: "the best",
          tip: "THE BEST! Irregular superlative! ❌ NOT 'goodest'! Good → better → THE BEST!"
        },
        {
          type: "input",
          q: "Complete: 'That was ___ movie I ever saw!' (the worst)",
          a: "the worst"
        },
        {
          type: "order",
          q: "Ordena: 'Esta es la mejor opción'",
          words: ["This", "is", "the", "best", "option."],
          a: "This is the best option."
        },
        {
          type: "mc",
          q: "'Which is ___ season - spring, summer, fall, or winter?' (el mejor)",
          options: ["the best", "the goodest", "best"],
          a: "the best",
          successtip: "✓ THE BEST! Superlative with WHICH! 'Which is the best?' = asking for #1 choice from multiple options!"
        },
        {
          type: "input",
          q: "Complete: 'Monday is ___ day of the week for me.' (the worst)",
          a: "the worst"
        },
        {
          type: "mc",
          q: "'Alaska is ___ state from here.' (el más lejano)",
          options: ["the farthest / the furthest", "the farest", "the most far"],
          a: "the farthest / the furthest",
          tip: "THE FARTHEST/FURTHEST! Both correct! Distance = farthest. Additional info = furthest. Both work for distance!"
        },
        {
          type: "order",
          q: "Ordena: '¿Cuál es la peor película que has visto?'",
          words: ["What", "is", "the", "worst", "movie", "you", "know?"],
          a: "What is the worst movie you know?"
        },
        {
          type: "input",
          q: "Complete: 'In my opinion, summer is ___ season.' (the best)",
          a: "the best"
        },
        {
          type: "mc",
          q: "Complete progression: good → ___ → ___",
          options: ["better → the best", "gooder → the goodest", "more good → the most good"],
          a: "better → the best",
          successtip: "✓ FULL PROGRESSION! 🎯 Good → better (comparative) → THE best (superlative). Bad → worse → THE worst. Far → farther/further → THE farthest/furthest. These are THE MOST COMMON irregulars in English!"
        }
      ]
    },

    // SET 6: MINI-READING 1 - Restaurant Choice
    {
      id: "m4-u18-set6",
      title: "Set 6: Reading - Restaurant Choice",
      questions: [
        {
          type: "mc",
          q: "Lee la conversación:\n\n**Choosing a Restaurant**\n\n**Sarah:** I'm hungry! Which restaurant do you prefer - Italian or Chinese?\n\n**Mike:** Hmm... I like both! But I think Italian is better for me tonight. I'm not feeling well, and Chinese food is sometimes too spicy.\n\n**Sarah:** Good choice! What about Pizza Palace or Roma Trattoria? Which one do you like?\n\n**Mike:** I don't know both restaurants. Which one has better reviews?\n\n**Sarah:** Roma Trattoria has the best reviews in the city! People say it's better than Pizza Palace. The food is good at Pizza Palace, but Roma's food is better.\n\n**Mike:** Perfect! Is it far from here?\n\n**Sarah:** No! Pizza Palace is farther - it's 5 km away. Roma is closer - only 2 km.\n\n**Mike:** Great! Roma Trattoria is the best choice! Better food and closer!\n\n**Sarah:** I agree! That's the best decision!\n\nSarah asks 'Which restaurant' because:",
          options: [
            "She's offering 2 specific choices (Italian OR Chinese) - limited options",
            "She doesn't know any restaurants",
            "She's asking an open question"
          ],
          a: "She's offering 2 specific choices (Italian OR Chinese) - limited options",
          tip: "WHICH = specific options! Sarah says 'Italian OR Chinese' = 2 clear choices = use WHICH!"
        },
        {
          type: "mc",
          q: "Mike says 'better' (not 'more good') because:",
          options: [
            "GOOD → BETTER is irregular comparative",
            "Better is wrong",
            "More good is correct"
          ],
          a: "GOOD → BETTER is irregular comparative",
          tip: "BETTER! Irregular! Mike compares Italian vs Chinese for tonight = comparative needed!"
        },
        {
          type: "mc",
          q: "'Roma has THE BEST reviews' uses:",
          options: [
            "Irregular superlative (good → the best)",
            "Regular comparative",
            "Wrong grammar"
          ],
          a: "Irregular superlative (good → the best)",
          successtip: "✓ THE BEST! Superlative! Comparing Roma to ALL restaurants in city = superlative!"
        },
        {
          type: "mc",
          q: "'Pizza Palace is FARTHER' uses:",
          options: [
            "Irregular comparative (far → farther)",
            "Regular comparative with -ER",
            "Wrong word"
          ],
          a: "Irregular comparative (far → farther)",
          tip: "FARTHER! Irregular comparative! Comparing distances: Pizza Palace (5km) vs Roma (2km)!"
        },
        {
          type: "mc",
          q: "How many grammar structures appear? (WHICH, OR, AND, irregular comparatives/superlatives)",
          options: [
            "ALL of them! WHICH questions, OR choices, AND connections, better/best/farther",
            "Only one structure",
            "No grammar"
          ],
          a: "ALL of them! WHICH questions, OR choices, AND connections, better/best/farther",
          successtip: "✓ COMPLETE INTEGRATION! Real conversation using ALL Unit 18 structures naturally! This is how native speakers make decisions!"
        }
      ]
    },

    // SET 7: Combining WHICH/WHAT with Comparatives
    {
      id: "m4-u18-set7",
      title: "Set 7: WHICH/WHAT + Comparatives & Superlatives",
      questions: [
        {
          type: "match",
          q: "Match question patterns:",
          pairs: [
            { left: "Which is better - A or B?", right: "WHICH + comparative + choice" },
            { left: "What is the best option?", right: "WHAT + superlative + general" },
            { left: "Which is the best - A, B, or C?", right: "WHICH + superlative + specific options" }
          ],
          a: "completed",
          tip: "Powerful combinations! WHICH/WHAT + comparatives/superlatives = decision-making questions!"
        },
        {
          type: "mc",
          q: "'___ city is better - Toronto or Montreal?'",
          options: ["Which", "What", "Where"],
          a: "Which",
          tip: "WHICH! Two specific cities + comparative (better) = WHICH is better!"
        },
        {
          type: "input",
          q: "Complete: '___ is the best restaurant in town?' (What)",
          a: "What"
        },
        {
          type: "order",
          q: "Ordena: '¿Cuál es peor - este o ese?'",
          words: ["Which", "is", "worse", "-", "this", "or", "that?"],
          a: "Which is worse - this or that?"
        },
        {
          type: "mc",
          q: "'___ season is the best in Canada?' (open question - many opinions)",
          options: ["What", "Which", "When"],
          a: "What",
          successtip: "✓ WHAT! Open question + superlative! 'What is the best?' = asking for opinion, not limiting to specific options!"
        },
        {
          type: "input",
          q: "Complete: 'Which hotel is ___ - the Ritz or the Plaza?' (better)",
          a: "better"
        },
        {
          type: "mc",
          q: "'___ is worse - cold weather or hot weather?' (comparing two)",
          options: ["Which", "What", "How"],
          a: "Which",
          tip: "WHICH! Two specific options (cold OR hot) + comparative (worse) = WHICH!"
        },
        {
          type: "order",
          q: "Ordena: '¿Qué es lo mejor de vivir aquí?'",
          words: ["What", "is", "the", "best", "thing", "about", "living", "here?"],
          a: "What is the best thing about living here?"
        },
        {
          type: "input",
          q: "Complete: 'Which movie is the ___ - A, B, or C?' (best)",
          a: "best"
        },
        {
          type: "mc",
          q: "Natural pattern: 'Which is [comparative] - A or B?' vs 'Which is [superlative] - A, B, or C?'",
          options: [
            "Comparative for 2 options. Superlative for 3+ options.",
            "Always use superlative",
            "They're random"
          ],
          a: "Comparative for 2 options. Superlative for 3+ options.",
          successtip: "✓ LOGIC! 2 things = comparative (which is better - A or B?). 3+ things = superlative (which is best - A, B, or C?). Natural progression!"
        }
      ]
    },

    // SET 8: MINI-READING 2 - Hotel Comparison
    {
      id: "m4-u18-set8",
      title: "Set 8: Reading - Hotel Comparison",
      questions: [
        {
          type: "mc",
          q: "Lee el texto:\n\n**Booking a Hotel - Online Review**\n\n**User: TravelLover99**\n\n*Comparing three hotels in Toronto - which is the best?*\n\nI'm planning a trip to Toronto and I can't decide which hotel to book! I'm comparing three options:\n\n**The Ritz-Carlton** - This is the most expensive hotel ($800/night), but it has the best reviews. The location is good - it's downtown. The rooms are beautiful and the service is excellent.\n\n**The Fairmont Royal York** - This hotel is cheaper than the Ritz ($300/night). The reviews are good, but not as good as the Ritz. The location is better than the Ritz - it's right next to Union Station! The building is older but more beautiful and historic.\n\n**Hotel X Toronto** - This is the cheapest option ($250/night). The reviews are worse than the Royal York, but they're not bad. The location is the worst - it's far from downtown (30 minutes by car). But it has the best pool and it's good for families.\n\nWhich is the best choice? It depends!\n\n- If money is not important and you want the best quality → The Ritz is the best!\n- If you want good quality and a better location → The Royal York is better!\n- If you want the cheapest option and you have a family → Hotel X is the best!\n\nAll three hotels are good, but they're better for different people.\n\nHow many hotels are compared?",
          options: [
            "Three hotels (Ritz, Royal York, Hotel X)",
            "Two hotels",
            "Five hotels"
          ],
          a: "Three hotels (Ritz, Royal York, Hotel X)"
        },
        {
          type: "mc",
          q: "The Ritz-Carlton is:",
          options: [
            "The most expensive ($800) with the best reviews",
            "The cheapest",
            "The worst"
          ],
          a: "The most expensive ($800) with the best reviews",
          tip: "SUPERLATIVES! Most expensive + best reviews = highest quality, highest price!"
        },
        {
          type: "mc",
          q: "'The location is BETTER than the Ritz' - this uses:",
          options: [
            "Irregular comparative (good → better)",
            "Regular comparative",
            "Superlative"
          ],
          a: "Irregular comparative (good → better)",
          successtip: "✓ BETTER! Comparing Royal York location vs Ritz location (2 things) = comparative!"
        },
        {
          type: "mc",
          q: "'The reviews are WORSE than the Royal York' - this uses:",
          options: [
            "Irregular comparative (bad → worse)",
            "Regular comparative",
            "Superlative"
          ],
          a: "Irregular comparative (bad → worse)",
          tip: "WORSE! Comparing Hotel X reviews vs Royal York reviews (2 things) = comparative!"
        },
        {
          type: "mc",
          q: "Why does the review say 'it depends'?",
          options: [
            "Different hotels are 'the best' for different priorities (quality vs location vs price vs family)",
            "All hotels are exactly the same",
            "No hotel is good"
          ],
          a: "Different hotels are 'the best' for different priorities (quality vs location vs price vs family)",
          successtip: "✓ CONTEXT MATTERS! 'The best' depends on YOUR priority! Quality? → Ritz. Location? → Royal York. Price? → Hotel X. No single 'best' for everyone!"
        }
      ]
    },

    // SET 9: Advanced Structures (Either/Neither/Both)
    {
      id: "m4-u18-set9",
      title: "Set 9: Advanced Choice Expressions",
      questions: [
        {
          type: "match",
          q: "Match advanced choice expressions (native idioms!):",
          pairs: [
            { left: "Either A or B", right: "One or the other (uno u otro)" },
            { left: "Neither A nor B", right: "Not this and not that (ni A ni B)" },
            { left: "Both A and B", right: "This and that together (ambos)" }
          ],
          a: "completed",
          tip: "Advanced structures! Either/neither/both = emphatic ways to express choices! Very native!"
        },
        {
          type: "mc",
          q: "'You can choose ___ coffee ___ tea.' (one or the other)",
          options: ["either / or", "neither / nor", "both / and"],
          a: "either / or",
          tip: "EITHER...OR! One or the other! 'Either A or B' = pick one option!"
        },
        {
          type: "input",
          q: "Complete: 'I like ___ summer ___ winter.' (both)",
          a: "both / and"
        },
        {
          type: "order",
          q: "Ordena: 'Ni Toronto ni Montreal son baratos'",
          words: ["Neither", "Toronto", "nor", "Montreal", "is", "cheap."],
          a: "Neither Toronto nor Montreal is cheap."
        },
        {
          type: "mc",
          q: "'I don't like ___ coffee ___ tea.' (not this, not that)",
          options: ["neither / nor", "either / or", "both / and"],
          a: "neither / nor",
          successtip: "✓ NEITHER...NOR! Negative structure! 'Neither A nor B' = I don't like A, and I don't like B. Not this, not that!"
        },
        {
          type: "input",
          q: "Complete: 'You can visit ___ the museum ___ the park.' (either / or)",
          a: "either / or"
        },
        {
          type: "mc",
          q: "'___ restaurants are expensive.' (talking about two restaurants together)",
          options: ["Both", "Either", "Neither"],
          a: "Both",
          tip: "BOTH! Talking about TWO together! 'Both restaurants' = the two restaurants (together)!"
        },
        {
          type: "order",
          q: "Ordena: 'Puedes elegir pizza o pasta'",
          words: ["You", "can", "choose", "either", "pizza", "or", "pasta."],
          a: "You can choose either pizza or pasta."
        },
        {
          type: "input",
          q: "Complete: '___ Toronto ___ Montreal have beautiful architecture.' (Both / and)",
          a: "Both / and"
        },
        {
          type: "mc",
          q: "Practice context: 'I want to visit Europe, but I can't decide. I like ___ France ___ Italy!' (I like both)",
          options: ["both / and", "either / or", "neither / nor"],
          a: "both / and",
          successtip: "✓ ADVANCED MASTERY! Either/or = choice (uno u otro). Neither/nor = rejection (ni uno ni otro). Both/and = inclusion (ambos). These make you sound MORE native! 🎯"
        }
      ]
    },

    // SET 10: MINI-READING 3 - Shopping Decision
    {
      id: "m4-u18-set10",
      title: "Set 10: Reading - Shopping Decision",
      questions: [
        {
          type: "mc",
          q: "Lee la conversación:\n\n**At the Electronics Store**\n\n**Customer:** Hi! I want to buy a laptop. Which one is better - this one or that one?\n\n**Salesperson:** Well, it depends on what you need! Both laptops are good, but they're different.\n\n**Customer:** What's the difference?\n\n**Salesperson:** This one is cheaper ($800), but that one has better performance. That one is $1,200.\n\n**Customer:** Hmm... Which has the best battery life?\n\n**Salesperson:** That one! The expensive laptop has the best battery - 12 hours. This cheaper one has good battery too - 8 hours. But the expensive one is better.\n\n**Customer:** What about size? Which is lighter?\n\n**Salesperson:** The expensive one is lighter. It's 1.2 kg. The cheaper one is 1.8 kg.\n\n**Customer:** So the expensive one is better in every way?\n\n**Salesperson:** Yes, but the cheaper one is also good! If price is important, the cheaper one is the best choice. If quality is important, the expensive one is the best.\n\n**Customer:** I think quality is more important. The expensive one is the best option for me!\n\n**Salesperson:** Great choice! And we have a promotion - you can get either a free mouse or a free bag with your purchase!\n\n**Customer:** Perfect! I'll take the free bag!\n\nThe customer asks 'Which one is better' because:",
          options: [
            "He's comparing 2 specific laptops in front of him",
            "He's asking an open question",
            "He doesn't want to buy"
          ],
          a: "He's comparing 2 specific laptops in front of him",
          tip: "WHICH + BETTER! Two specific laptops = limited choice = use WHICH + comparative!"
        },
        {
          type: "mc",
          q: "'Which has the BEST battery life?' uses:",
          options: [
            "WHICH + irregular superlative (the best)",
            "WHAT + comparative",
            "WHERE + superlative"
          ],
          a: "WHICH + irregular superlative (the best)",
          tip: "WHICH + THE BEST! Asking which laptop (from the two) has the best battery!"
        },
        {
          type: "mc",
          q: "The salesperson says 'BOTH laptops are good' meaning:",
          options: [
            "The two laptops together - both are good quality",
            "Only one laptop is good",
            "No laptop is good"
          ],
          a: "The two laptops together - both are good quality",
          successtip: "✓ BOTH! Means 'the two together'! Not choosing - describing BOTH!"
        },
        {
          type: "mc",
          q: "'You can get EITHER a mouse OR a bag' uses:",
          options: [
            "Either...or structure (choose one of two options)",
            "Both...and structure",
            "Neither...nor structure"
          ],
          a: "Either...or structure (choose one of two options)",
          tip: "EITHER...OR! Advanced structure! 'Either A or B' = pick one free gift!"
        },
        {
          type: "mc",
          q: "What determines 'the best' choice?",
          options: [
            "It depends! If price matters = cheaper is best. If quality matters = expensive is best. Context determines 'best'!",
            "Expensive is always best",
            "Cheap is always best"
          ],
          a: "It depends! If price matters = cheaper is best. If quality matters = expensive is best. Context determines 'best'!",
          successtip: "✓ CONTEXT IS KEY! 'The best' depends on YOUR priority! Real-life decision-making! Price vs quality = different 'best' answers!"
        }
      ]
    }
  ],
// ===================================================================
// READING SET: Planning a Vacation (Decision Making)
// ===================================================================
readingSet: {
  id: "m4-u18-reading",
  title: "Set 11: Full Reading - Planning a Vacation",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: The Three Options (preguntas 1, 2 y 3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Vacation Brainstorming**

**From: Emma | To: Carlos**
Hi Carlos! I'm so excited about our summer vacation! But we need to decide—which destination is better for us? I'm comparing three options:

**COSTA RICA:** $2,000 per person. Hot and rainy (rainy season). Best nature and wildlife! Rainforests are amazing.
**HAWAII:** $4,000 per person. Perfect weather! Hot and sunny. Flight is 10 hours from Toronto.
**MEXICO (Cancun):** $1,500 per person. Very hot and sunny. Only 4 hours from Toronto. Beaches are beautiful.`
    },
    {
      type: "mc",
      q: "¿Cuántas opciones de vacaciones están comparando Emma y Carlos?",
      options: [
        "Three options (Costa Rica, Hawaii, Mexico)",
        "Two options",
        "Five options"
      ],
      a: "Three options (Costa Rica, Hawaii, Mexico)",
      successtip: "✓ OPTIONS IDENTIFIED!\nCosta Rica, Hawaii, and Mexico are on the table. ✈️"
    },
    {
      type: "mc",
      q: "¿Cuál de los destinos es el más caro (**THE MOST EXPENSIVE**)?",
      options: [
        "Hawaii ($4,000 per person)",
        "Costa Rica ($2,000)",
        "Mexico ($1,500)"
      ],
      a: "Hawaii ($4,000 per person)",
      tip: "Superlativo: Hawaii tiene el precio más alto de los tres. 💰",
      successtip: "✓ MOST EXPENSIVE!\n$4,000 is the highest price mentioned. 💎"
    },
    {
      type: "mc",
      q: "¿Cuál de los destinos es el más barato (**THE CHEAPEST**)?",
      options: [
        "Mexico ($1,500 per person)",
        "Hawaii",
        "Costa Rica"
      ],
      a: "Mexico ($1,500 per person)",
      tip: "Superlativo: Mexico tiene el precio más bajo. 💸",
      successtip: "✓ CHEAPEST!\nMexico is the most budget-friendly option. 🇲🇽"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Comparing Features (preguntas 4, 5 y 6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Comparing the Details**

Hawaii has **better** beaches than Costa Rica, and the weather is much **better**—it's sunny, not rainy. However, Costa Rica is cheaper than Hawaii. 

But be careful with Costa Rica in summer: the weather is **worse** than the other options because it rains a lot. Also, Hawaii is **farther** from Toronto than Mexico.`
    },

    

    {
      type: "mc",
      q: "La frase 'Hawaii has BETTER beaches than Costa Rica' utiliza:",
      options: [
        "Un comparativo irregular (good → better)",
        "Un comparativo regular",
        "Un superlativo"
      ],
      a: "Irregular comparative (good → better)",
      successtip: "✓ BETTER!\nEstás comparando dos cosas (playas de Hawaii vs Costa Rica). 🏖️"
    },
    {
      type: "mc",
      q: "La frase 'The weather is WORSE than the other options' utiliza:",
      options: [
        "Un comparativo irregular (bad → worse)",
        "Un comparativo regular",
        "Un superlativo"
      ],
      a: "Irregular comparative (bad → worse)",
      tip: "Worse es el comparativo de 'bad'. Compara el clima lluvioso con el soleado. ☔",
      successtip: "✓ WORSE!\nCorrecto. El clima de Costa Rica es 'peor' en verano. ⛈️"
    },
    {
      type: "mc",
      q: "En 'Hawaii is FARTHER from Toronto', se está comparando:",
      options: [
        "Distancia (far → farther)",
        "Precio",
        "Calidad"
      ],
      a: "Irregular comparative (far → farther)",
      tip: "Farther indica mayor distancia física (10 horas vs 4 horas). 🗺️",
      successtip: "✓ FARTHER!\n¡Exacto! Hawaii está más lejos de Toronto que los otros destinos. ✈️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: The Decision (preguntas 7 y 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Emma's Conclusion**

I think Mexico is **the best** option for us! It's the cheapest and the closest. Yes, Hawaii has **better** weather, and Costa Rica has **better** nature, but Mexico has **the best** combination of price, distance, and activities! What do you think?`
    },
    {
      type: "mc",
      q: "¿Por qué Emma cree que México es 'la mejor' (**the best**) opción?",
      options: [
        "Por la mejor COMBINACIÓN de precio, distancia y clima (balance)",
        "Porque es perfecto en absolutamente todo",
        "Porque es la única opción que conoce"
      ],
      a: "Best COMBINATION of price, distance, weather, and activities (balance!)",
      successtip: "✓ BALANCE!\nNo es el mejor en una sola cosa, sino el más equilibrado para ellos. ⚖️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Carlos' Reply & Choices (preguntas 8 y 9)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Carlos' Response**

I agree! Let's book Mexico! I think we should visit **both** Cancun **and** Tulum. Cancun has better nightlife, but Tulum is more peaceful. 

**Either** we stay in Cancun the whole time, **or** we split the trip—4 days in Cancun and 3 days in Tulum. I prefer the second option!`
    },
    {
      type: "mc",
      q: "Carlos sugiere visitar 'BOTH Cancun AND Tulum'. Esto significa:",
      options: [
        "Visitar las dos ciudades (no elegir solo una)",
        "Elegir solo una de las dos",
        "No visitar ninguna"
      ],
      a: "Visit the two cities together (not choosing one)",
      tip: "Both...and = Ambos / Los dos. 🤝",
      successtip: "✓ BOTH...AND!\n¡Exacto! Quiere disfrutar de lo mejor de ambos lugares. 🏙️🏖️"
    },
    {
      type: "mc",
      q: "Carlos ofrece: 'EITHER we stay in Cancun OR we split the trip'. Esto significa:",
      options: [
        "Elegir una opción o la otra (uno u otro)",
        "Hacer las dos opciones al mismo tiempo",
        "No hacer ninguna"
      ],
      a: "Choose one option or the other (uno u otro)",
      successtip: "✓ EITHER...OR!\nEs una estructura de elección entre dos alternativas. 🎯"
    },
    {
      type: "mc",
      q: "En resumen, ¿cuántas estructuras de la Unidad 18 aparecen en este texto?",
      options: [
        "Casi todas (comparativos y superlativos irregulares, either/or, both/and)",
        "Solo una o dos",
        "Ninguna estructura gramatical"
      ],
      a: "ALL structures! WHICH vs WHAT, OR choices, AND connections, irregular comparatives (better/worse/farther), irregular superlatives (best/worst/most expensive/cheapest), either...or, both...and",
      successtip: "✓ COMPLETE MASTERY! 🎉\n¡Felicidades! Has analizado una conversación real que usa toda la gramática de toma de decisiones. 🏆"
    }
  ]
},
  // SET 12: Review
  reviewSet: {
    id: "m4-u18-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Match key concepts:",
        pairs: [
          { left: "WHICH", right: "Limited/specific options" },
          { left: "WHAT", right: "Open/general question" },
          { left: "Irregular comparatives", right: "better, worse, farther" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "Rule: When to use WHICH?",
        options: [
          "When you have limited/specific options (A or B?)",
          "Never use WHICH",
          "Always use WHICH"
        ],
        a: "When you have limited/specific options (A or B?)"
      },
      {
        type: "mc",
        q: "Complete: good → ___ → the best",
        options: ["better", "gooder", "more good"],
        a: "better"
      },
      {
        type: "input",
        q: "Complete: bad → ___ → the worst",
        a: "worse"
      },
      {
        type: "input",
        q: "Complete: far → ___ / ___ → the farthest/furthest",
        a: "farther / further"
      },
      {
        type: "order",
        q: "Ordena: '¿Cuál es mejor - este o ese?'",
        words: ["Which", "is", "better", "-", "this", "or", "that?"],
        a: "Which is better - this or that?"
      },
      {
        type: "mc",
        q: "'OR' is used for:",
        options: [
          "Showing choice between options",
          "Connecting things together",
          "Negative statements"
        ],
        a: "Showing choice between options"
      },
      {
        type: "mc",
        q: "'AND' is used for:",
        options: [
          "Connecting multiple things together (both/all)",
          "Showing choices",
          "Questions only"
        ],
        a: "Connecting multiple things together (both/all)"
      },
      {
        type: "mc",
        q: "'Either...or' means:",
        options: [
          "One or the other (choose one option)",
          "Both options together",
          "Neither option"
        ],
        a: "One or the other (choose one option)"
      },
      {
        type: "mc",
        q: "FINAL MASTERY - Complete conversation:\n\n**A:** 'I can't decide! ___ city do you prefer - Toronto ___ Montreal?'\n**B:** 'Hmm... ___ cities are beautiful! But I think Toronto is ___ than Montreal.'\n**A:** 'Why is Toronto ___?'\n**B:** 'Toronto has ___ job opportunities. It's ___ for work. But Montreal has ___ food and culture!'\n**A:** '___ is the best city in Canada?'\n**B:** 'That depends! If you want jobs, Toronto is ___. If you want culture, Montreal is ___. ___ cities have good and bad points. You can choose ___ Toronto ___ Montreal - ___ are good choices!'\n\nCorrect words:",
        options: [
          "Which / or / Both / better / better / better / better / better / What / the best / the best / Both / either / or / both",
          "What / and / Either / good / good / good / good / good / Which / good / good / Either / neither / nor / neither",
          "Where / but / Neither / worse / worse / worse / worse / worse / Who / worst / worst / Neither / both / and / either"
        ],
        a: "Which / or / Both / better / better / better / better / better / What / the best / the best / Both / either / or / both",
        successtip: "✓ UNIT 18 COMPLETE! Making choices mastered! 🎯 You learned: WHICH vs WHAT (limited vs open questions), OR for choices, AND for connections, irregular comparatives (good→better, bad→worse, far→farther/further - NOT gooder/badder/farer!), irregular superlatives (the best, the worst, the farthest/furthest - NOT goodest/baddest!), advanced structures (either...or = uno u otro, neither...nor = ni uno ni otro, both...and = ambos), combining WHICH/WHAT with comparatives/superlatives ('Which is better?' 'What is the best?'), real decision-making conversations! From basic choices to complex decision-making! You can now make, explain, and discuss choices like a native speaker! 🏆🗣️✅"
      }
    ]
  }
};
